import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CompactEditDialog.css';

const CompactEditDialog = ({
  measurement,
  ablaufDbData = [],
  einflussDbData = [],
  dependentListsData = [],
  timeTypes = [],
  grades = [0, 80, 90, 100, 105, 110, 115, 120, 125],
  onSave,
  onCancel,
  onDelete,
  formatTime,
  darkMode = false,
  selectedSession = null,
  onPhotoUpload = null,
  onPhotoDelete = null,
  measurementPhotos = {},
  loadPhotos = null
}) => {
  const { t } = useTranslation();
  const [editActivity, setEditActivity] = useState(measurement?.activity || '');
  const [editNote, setEditNote] = useState(measurement?.note || '');
  const [editExcluded, setEditExcluded] = useState(measurement?.excluded || false);
  const [editTimeType, setEditTimeType] = useState(measurement?.timeType || '');
  const [editGrade, setEditGrade] = useState(measurement?.grade || null);
  const [editEinflussgroessenValues, setEditEinflussgroessenValues] = useState({});
  const [selectedKuerzel, setSelectedKuerzel] = useState(null);
  const [editPhotos, setEditPhotos] = useState([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isListeningEdit, setIsListeningEdit] = useState(false);
  const [, setIsListeningEditActivity] = useState(false);
  const [recognitionEdit] = useState(null);
  const isListeningEditRef = React.useRef(false);
  const isListeningEditActivityRef = React.useRef(false);
  const photoInputRef = React.useRef(null);
  // Refs für temporäre Werte beim Fokus (um alle datalist-Optionen anzuzeigen)
  const originalEinflussValuesRef = React.useRef({});
  const focusedEinflussRef = React.useRef(null);

  const normalizeMesswerte = React.useCallback((values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((mw) => (typeof mw === 'string' ? mw : (mw?.messwert || '')))
      .map((v) => (v || '').toString().trim())
      .filter((v) => v);
  }, []);

  const getDependentOptionValues = React.useCallback((einfluss) => {
    if (!einfluss || !einfluss.einflussname || !selectedKuerzel?.einflussgroessen) return [];
    const targetName = (einfluss.einflussname || '').toString().trim();
    const sheetName = (einfluss.einheit || '').toString().trim();
    if (!targetName || !sheetName) return [];
    const sheet = (dependentListsData || []).find((s) => (s?.name || '').trim() === sheetName);
    if (!sheet || !Array.isArray(sheet.columns) || !Array.isArray(sheet.rows) || sheet.columns.length === 0) return [];
    const targetIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === targetName);
    if (targetIdx < 0) return [];

    const fields = (selectedKuerzel.einflussgroessen || [])
      .filter((f) => {
        const typ = ((f?.typ || f?.klassewert || '').toString().trim().charAt(0) || '');
        const fSheet = (f?.einheit || '').toString().trim();
        return (typ === 'l' || typ === 'L') && fSheet === sheetName;
      })
      .map((f) => (f?.einflussname || '').toString().trim())
      .filter((n) => n);

    const rows = (sheet.rows || []).filter((row) => {
      if (!Array.isArray(row)) return false;
      return fields.every((fieldName) => {
        if (fieldName === targetName) return true;
        const selected = (editEinflussgroessenValues?.[fieldName] || '').toString().trim();
        if (!selected) return true;
        const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === fieldName);
        if (colIdx < 0) return true;
        return ((row[colIdx] || '').toString().trim() === selected);
      });
    });
    return Array.from(new Set(rows.map((row) => (row[targetIdx] || '').toString().trim()).filter((v) => v)));
  }, [dependentListsData, editEinflussgroessenValues, selectedKuerzel]);

  const getEinflussOptionValues = React.useCallback((einfluss) => {
    if (!einfluss || !einfluss.einflussname) return [];
    const name = einfluss.einflussname.trim();
    if (!name) return [];
    const typRaw = (einfluss.typ || einfluss.klassewert || '').toString().trim();
    const typ = typRaw ? typRaw.charAt(0) : '';
    const isDependentListType = typ === 'l' || typ === 'L';
    const isEinflussDbType = typ === 'x' || typ === 'X';

    if (isDependentListType) {
      const dependentOptions = getDependentOptionValues(einfluss);
      if (dependentOptions.length > 0) {
        return dependentOptions;
      }
      return normalizeMesswerte(einfluss.messwerte);
    }

    if (isEinflussDbType) {
      const dbEntry = (einflussDbData || []).find((item) => (item?.name || '').trim() === name);
      if (dbEntry?.messwerte) {
        return normalizeMesswerte(dbEntry.messwerte);
      }
      return normalizeMesswerte(einfluss.messwerte);
    }

    return normalizeMesswerte(einfluss.messwerte);
  }, [einflussDbData, getDependentOptionValues, normalizeMesswerte]);

  const applyEditDependentAutofill = React.useCallback((nextValues) => {
    if (!selectedKuerzel?.einflussgroessen) return nextValues;
    const result = { ...(nextValues || {}) };
    const sheets = Array.from(new Set(
      (selectedKuerzel.einflussgroessen || [])
        .map((f) => {
          const typ = ((f?.typ || f?.klassewert || '').toString().trim().charAt(0) || '');
          const sName = (f?.einheit || '').toString().trim();
          return (typ === 'l' || typ === 'L') ? sName : '';
        })
        .filter((s) => s)
    ));

    sheets.forEach((sheetName) => {
      const sheet = (dependentListsData || []).find((s) => (s?.name || '').trim() === sheetName);
      if (!sheet || !Array.isArray(sheet.columns) || !Array.isArray(sheet.rows) || sheet.columns.length === 0) return;

      const fields = (selectedKuerzel.einflussgroessen || [])
        .filter((f) => {
          const typ = ((f?.typ || f?.klassewert || '').toString().trim().charAt(0) || '');
          const sName = (f?.einheit || '').toString().trim();
          return (typ === 'l' || typ === 'L') && sName === sheetName;
        })
        .map((f) => (f?.einflussname || '').toString().trim())
        .filter((n) => n);

      const matchingRows = (sheet.rows || []).filter((row) => {
        if (!Array.isArray(row)) return false;
        return fields.every((fieldName) => {
          const selected = (result[fieldName] || '').toString().trim();
          if (!selected) return true;
          const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === fieldName);
          if (colIdx < 0) return true;
          return ((row[colIdx] || '').toString().trim() === selected);
        });
      });

      if (matchingRows.length === 1) {
        const tuple = matchingRows[0];
        fields.forEach((fieldName) => {
          const current = (result[fieldName] || '').toString().trim();
          if (current) return;
          const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === fieldName);
          if (colIdx < 0) return;
          const tupleValue = (tuple[colIdx] || '').toString().trim();
          if (tupleValue) result[fieldName] = tupleValue;
        });
      }
    });

    return result;
  }, [dependentListsData, selectedKuerzel]);

  const applyEditEinflussValueChange = React.useCallback((name, value) => {
    const key = (name || '').toString().trim();
    if (!key) return;
    setEditEinflussgroessenValues((prev) => applyEditDependentAutofill({ ...(prev || {}), [key]: value }));
  }, [applyEditDependentAutofill]);

  // Initialisiere Werte beim Öffnen
  useEffect(() => {
    if (measurement) {
      setEditActivity(measurement.activity || '');
      setEditNote(measurement.note || '');
      setEditExcluded(measurement.excluded || false);
      setEditTimeType(measurement.timeType || '');
      setEditGrade(measurement.grade || null);
      
      // Lade Einflussgrößen-Werte
      const einflussValues = measurement.einflussgroessen || {};
      setEditEinflussgroessenValues(einflussValues);
      
      // Finde zugehöriges Kürzel
      // Verwende zuerst das gespeicherte kuerzel-Feld, falls vorhanden
      if (ablaufDbData && ablaufDbData.length > 0) {
        let matchingKuerzel = null;
        
        // Wenn kuerzel-Feld vorhanden, suche direkt danach
        if (measurement.kuerzel) {
          matchingKuerzel = ablaufDbData.find(k => 
            k && k.kuerzel && k.kuerzel.trim() === measurement.kuerzel.trim()
          );
        }
        
        // Falls nicht gefunden, suche über activity (für Rückwärtskompatibilität)
        if (!matchingKuerzel) {
          matchingKuerzel = ablaufDbData.find(k => 
            k.beschreibung === measurement.activity || 
            k.fullText === measurement.activity ||
            k.kuerzel === measurement.activity
          );
        }
        
        if (matchingKuerzel) {
          setSelectedKuerzel(matchingKuerzel);
          
          // Initialisiere auch leere Werte für alle Einflussgrößen des Kürzels, falls noch nicht vorhanden
          setEditEinflussgroessenValues(prev => {
            const updated = { ...prev };
            if (matchingKuerzel.einflussgroessen && Array.isArray(matchingKuerzel.einflussgroessen)) {
              matchingKuerzel.einflussgroessen.forEach(einfluss => {
                if (einfluss && einfluss.einflussname) {
                  const name = einfluss.einflussname.trim();
                  if (name && !updated[name]) {
                    updated[name] = '';
                  }
                }
              });
            }
            return applyEditDependentAutofill(updated);
          });
        }
      }
      
      // Lade Fotos für diese Messung
      if (measurement.number && loadPhotos) {
        loadPhotos(measurement.number).then(photos => {
          setEditPhotos(photos.map(photo => ({
            id: photo.id,
            fileData: photo.fileData,
            fileName: photo.fileName
          })));
        }).catch(error => {
          console.error('Fehler beim Laden der Fotos:', error);
          setEditPhotos([]);
        });
      } else {
        setEditPhotos([]);
      }
    }
  }, [measurement, ablaufDbData, loadPhotos, applyEditDependentAutofill]);

  // Aktualisiere selectedKuerzel wenn editActivity geändert wird
  useEffect(() => {
    // Nur ausführen, wenn editActivity vorhanden ist
    // ABER: Nicht ausführen, wenn selectedKuerzel bereits korrekt gesetzt wurde
    // (z.B. beim ersten Öffnen über measurement.kuerzel)
    if (editActivity && ablaufDbData && ablaufDbData.length > 0) {
      // Prüfe, ob selectedKuerzel bereits korrekt gesetzt wurde
      // (z.B. über measurement.kuerzel im ersten useEffect)
      if (selectedKuerzel && selectedKuerzel.kuerzel) {
        // Prüfe, ob editActivity noch zum aktuellen selectedKuerzel passt
        const activityMatches = 
          selectedKuerzel.beschreibung === editActivity ||
          selectedKuerzel.fullText === editActivity ||
          selectedKuerzel.kuerzel === editActivity;
        
        if (activityMatches) {
          // selectedKuerzel passt noch, nichts tun
          return;
        }
      }
      
      // selectedKuerzel passt nicht mehr oder ist nicht gesetzt, suche neues
      const matchingKuerzel = ablaufDbData.find(k => 
        k.beschreibung === editActivity || 
        k.fullText === editActivity ||
        k.kuerzel === editActivity
      );
      if (matchingKuerzel) {
        setSelectedKuerzel(matchingKuerzel);
        // Initialisiere leere Werte für Einflussgrößen
        setEditEinflussgroessenValues(prev => {
          const updated = { ...prev };
          if (matchingKuerzel.einflussgroessen && Array.isArray(matchingKuerzel.einflussgroessen)) {
            matchingKuerzel.einflussgroessen.forEach(einfluss => {
              if (einfluss && einfluss.einflussname) {
                const name = einfluss.einflussname.trim();
                if (name && !updated[name]) {
                  updated[name] = '';
                }
              }
            });
          }
          return applyEditDependentAutofill(updated);
        });
      }
      // WICHTIG: Nicht auf null setzen, wenn kein Match gefunden wird,
      // da selectedKuerzel möglicherweise bereits korrekt gesetzt wurde (z.B. über kuerzel-Feld)
    } else if (!editActivity) {
      setSelectedKuerzel(null);
    }
  }, [editActivity, ablaufDbData, selectedKuerzel, applyEditDependentAutofill]);

  // Handler für Foto-Upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !measurement || !selectedSession || !onPhotoUpload) return;
    
    if (!file.type.startsWith('image/')) {
      alert(t('alerts.photo_select'));
      e.target.value = '';
      return;
    }
    
    setIsUploadingPhoto(true);
    try {
      const photos = await onPhotoUpload(file, measurement.number);
      setEditPhotos(photos);
    } catch (error) {
      console.error('Fehler beim Hochladen des Fotos:', error);
      alert(t('alerts.photo_upload_error', { msg: error.message || t('feedback_status.unknown_error') }));
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Handler für Foto-Löschen
  const handleDeletePhoto = async (photoId) => {
    if (!measurement || !onPhotoDelete) return;
    
    try {
      const photos = await onPhotoDelete(photoId, measurement.number);
      setEditPhotos(photos);
    } catch (error) {
      console.error('Fehler beim Löschen des Fotos:', error);
      alert(t('alerts.photo_delete_error', { msg: error.message || t('feedback_status.unknown_error') }));
    }
  };

  // Handler für Sprach-Eingabe (Tätigkeit) – aktuell nicht an UI angebunden
  // eslint-disable-next-line no-unused-vars
  const handleStartListeningEditActivity = () => {
    if (!recognitionEdit) {
      alert(t('alerts.speech_not_supported'));
      return;
    }
    
    if (!isListeningEditActivityRef.current) {
      try {
        isListeningEditActivityRef.current = true;
        setIsListeningEditActivity(true);
        recognitionEdit.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        isListeningEditActivityRef.current = false;
        setIsListeningEditActivity(false);
      }
    }
  };

  // Handler für Sprach-Eingabe (Notiz)
  const handleStartListeningEdit = () => {
    if (!recognitionEdit) {
      alert(t('alerts.speech_not_supported'));
      return;
    }
    
    if (!isListeningEditRef.current) {
      try {
        isListeningEditRef.current = true;
        setIsListeningEdit(true);
        recognitionEdit.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        isListeningEditRef.current = false;
        setIsListeningEdit(false);
      }
    }
  };

  // Handler für Sprach-Eingabe stoppen
  const handleStopListeningEdit = () => {
    if (recognitionEdit && (isListeningEditRef.current || isListeningEditActivityRef.current)) {
      try {
        isListeningEditRef.current = false;
        isListeningEditActivityRef.current = false;
        setIsListeningEdit(false);
        setIsListeningEditActivity(false);
        recognitionEdit.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        isListeningEditRef.current = false;
        isListeningEditActivityRef.current = false;
        setIsListeningEdit(false);
        setIsListeningEditActivity(false);
      }
    }
  };

  const handleSave = () => {
    if (!measurement) return;
    
    // Sammle Einflussgrößen-Werte - nur nicht-leere Werte
    const einflussgroessen = {};
    if (editEinflussgroessenValues && Object.keys(editEinflussgroessenValues).length > 0) {
      Object.entries(editEinflussgroessenValues).forEach(([name, value]) => {
        // Nur Werte speichern, die nicht leer sind (explizit prüfen auf String und trim)
        if (value && typeof value === 'string' && value.trim().length > 0) {
          einflussgroessen[name] = value.trim();
        }
        // Leere Werte werden nicht hinzugefügt, damit sie entfernt werden
      });
    }
    
    // Finde das Kürzel für die aktualisierte Aktivität
    let updatedKuerzel = measurement.kuerzel || null;
    if (editActivity && ablaufDbData && ablaufDbData.length > 0) {
      const matchingKuerzel = ablaufDbData.find(k => 
        k.beschreibung === editActivity || 
        k.fullText === editActivity ||
        k.kuerzel === editActivity
      );
      if (matchingKuerzel) {
        updatedKuerzel = matchingKuerzel.kuerzel;
      } else {
        // Wenn keine Übereinstimmung gefunden, setze auf null
        updatedKuerzel = null;
      }
    }
    
    const updatedMeasurement = {
      ...measurement,
      activity: editActivity,
      kuerzel: updatedKuerzel, // Kürzel aktualisieren
      note: editNote,
      excluded: editExcluded,
      timeType: editTimeType,
      grade: editGrade,
      // Explizit setzen: wenn leer, dann undefined, sonst das Objekt
      // Dies entfernt gelöschte Ausprägungen, da sie nicht in einflussgroessen enthalten sind
      einflussgroessen: Object.keys(einflussgroessen).length > 0 ? einflussgroessen : undefined
    };
    
    onSave(updatedMeasurement);
  };

  const handleDelete = () => {
    // Übergebe das measurement-Objekt statt nur die ID (Dialog wird in App.jsx geöffnet)
    if (onDelete && measurement) {
      onDelete(measurement);
    }
  };

  if (!measurement) return null;

  return (
    <div className={`compact-edit-overlay ${darkMode ? 'dark-mode' : ''}`} onClick={onCancel}>
      <div className="compact-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="compact-edit-header">
          <h3>{t('compact.messpunkt_title', { number: measurement.number })}</h3>
          <button className="compact-edit-close" onClick={onCancel}>×</button>
        </div>
        
        <div className="compact-edit-content">
          {/* Tätigkeit */}
          <div className="compact-edit-field">
            <label>{t('compact.taetigkeit')}</label>
            <input
              type="text"
              value={editActivity}
              onChange={(e) => setEditActivity(e.target.value)}
              className="compact-edit-input"
              placeholder={t('compact.taetigkeit_placeholder')}
              list="compact-edit-kuerzel-datalist"
              autoCapitalize="none"
              autoCorrect="off"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && editActivity.trim() && ablaufDbData && ablaufDbData.length > 0) {
                  e.preventDefault();
                  // Suche nach Kürzel oder Beschreibung
                  const matchingKuerzel = ablaufDbData.find(k => 
                    k.kuerzel?.trim().toLowerCase() === editActivity.trim().toLowerCase() ||
                    k.beschreibung?.trim() === editActivity.trim() ||
                    k.fullText?.trim() === editActivity.trim()
                  );
                  if (matchingKuerzel) {
                    // Übernehme den Text und setze das Kürzel
                    setEditActivity(matchingKuerzel.beschreibung || matchingKuerzel.fullText || matchingKuerzel.kuerzel);
                    setSelectedKuerzel(matchingKuerzel);
                    // Setze auch die Zeitart, falls vorhanden
                    if (matchingKuerzel.zeitart && matchingKuerzel.zeitart.trim()) {
                      const zeitartExists = timeTypes.some(tt => tt.name === matchingKuerzel.zeitart.trim());
                      if (zeitartExists) {
                        setEditTimeType(matchingKuerzel.zeitart.trim());
                      }
                    }
                    // Initialisiere Einflussgrößen-Werte
                    setEditEinflussgroessenValues(prev => {
                      const updated = { ...prev };
                      if (matchingKuerzel.einflussgroessen && Array.isArray(matchingKuerzel.einflussgroessen)) {
                        matchingKuerzel.einflussgroessen.forEach(einfluss => {
                          if (einfluss && einfluss.einflussname) {
                            const name = einfluss.einflussname.trim();
                            if (name && !updated[name]) {
                              updated[name] = '';
                            }
                          }
                        });
                      }
                      return updated;
                    });
                  }
                }
              }}
            />
            {measurement.kuerzel && (
              <div style={{ 
                marginTop: '4px', 
                fontSize: '0.85rem', 
                color: '#6b7280',
                fontStyle: 'italic'
              }}>
                {t('compact.kuerzel')} {measurement.kuerzel}
              </div>
            )}
            {/* Datalist mit allen Kürzeln */}
            {ablaufDbData && ablaufDbData.length > 0 && (
              <datalist id="compact-edit-kuerzel-datalist">
                {ablaufDbData.map((item, idx) => (
                  <option key={idx} value={item.kuerzel}>
                    {item.beschreibung || item.fullText || item.kuerzel}
                  </option>
                ))}
              </datalist>
            )}
          </div>

          {/* Notiz */}
          <div className="compact-edit-field">
            <label>{t('compact.notiz')}</label>
            <textarea
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              className="compact-edit-textarea"
              placeholder={t('compact.notiz_placeholder')}
              rows="3"
            />
            <button
              type="button"
              className={`btn btn-voice ${isListeningEdit ? 'listening' : ''}`}
              onMouseDown={handleStartListeningEdit}
              onMouseUp={handleStopListeningEdit}
              onMouseLeave={handleStopListeningEdit}
              onTouchStart={handleStartListeningEdit}
              onTouchEnd={handleStopListeningEdit}
              disabled={!recognitionEdit}
              title={t('compact.voice_title')}
              style={{ marginTop: '8px', padding: '8px 12px', fontSize: '0.9rem' }}
            >
              {isListeningEdit ? `🎤 ${t('compact.voice_recording')}` : `🎤 ${t('compact.voice')}`}
            </button>
          </div>

          {/* Zeitart */}
          {timeTypes && timeTypes.length > 0 && (
            <div className="compact-edit-field">
              <label>{t('compact.zeitart')}</label>
              <select
                value={editTimeType}
                onChange={(e) => setEditTimeType(e.target.value)}
                className="compact-edit-select"
              >
                {timeTypes.map((tt) => (
                  <option key={tt.name} value={tt.name}>
                    {tt.name} - {tt.description || ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Leistungsgrad */}
          <div className="compact-edit-field">
            <label>{t('compact.leistungsgrad')}</label>
            <div className="compact-edit-grades">
              {grades.map((grade) => (
                <button
                  key={grade}
                  type="button"
                  className={`compact-edit-grade-btn ${editGrade === grade ? 'selected' : ''}`}
                  onClick={() => setEditGrade(grade)}
                >
                  {grade}
                </button>
              ))}
            </div>
          </div>

          {/* Ausgeklammert */}
          {(measurement.progressTime > 0 || measurement.grade > 0) && (
            <div className="compact-edit-field">
              <label>
                <input
                  type="checkbox"
                  checked={editExcluded}
                  onChange={(e) => setEditExcluded(e.target.checked)}
                />
                {t('compact.ausgeklammert')}
              </label>
            </div>
          )}

          {/* Einflussgrößen */}
          {selectedKuerzel && selectedKuerzel.einflussgroessen && Array.isArray(selectedKuerzel.einflussgroessen) && selectedKuerzel.einflussgroessen.length > 0 && (
            <div className="compact-edit-field">
              <label>{t('compact.einflussgroessen')}</label>
              <div className="compact-edit-einflussgroessen">
                {selectedKuerzel.einflussgroessen.map((einfluss, idx) => {
                  if (!einfluss || !einfluss.einflussname) return null;
                  const einflussname = einfluss.einflussname.trim();
                  if (!einflussname) return null;
                  const einheit = einfluss.einheit?.trim() || '';
                  const messwerte = getEinflussOptionValues(einfluss);
                  
                  return (
                    <div key={idx} className="compact-edit-einfluss-item">
                      <label className="compact-edit-einfluss-label">
                        {einflussname}
                        {einheit && <span className="compact-edit-einfluss-einheit"> ({einheit})</span>}
                      </label>
                      <input
                        type="text"
                        list={`compact-edit-datalist-${idx}`}
                        className="compact-edit-einfluss-input"
                        placeholder={messwerte.length > 0 ? t('compact.einfluss_placeholder_select') : t('compact.einfluss_placeholder_value')}
                        value={editEinflussgroessenValues?.[einflussname] || ''}
                        onFocus={(e) => {
                          // Speichere den ursprünglichen Wert
                          const currentValue = editEinflussgroessenValues?.[einflussname] || '';
                          originalEinflussValuesRef.current[einflussname] = currentValue;
                          focusedEinflussRef.current = einflussname;
                          // Leere temporär den Wert, damit alle datalist-Optionen angezeigt werden
                          if (currentValue) {
                            applyEditEinflussValueChange(einflussname, '');
                          }
                        }}
                        onBlur={(e) => {
                          const currentValue = e.target.value;
                          const originalValue = originalEinflussValuesRef.current[einflussname] || '';
                          const stateValue = editEinflussgroessenValues?.[einflussname] || '';
                          
                          // Nur wiederherstellen, wenn:
                          // 1. Der Input-Wert leer ist UND
                          // 2. Der State-Wert auch leer ist UND
                          // 3. Ursprünglich ein Wert vorhanden war
                          // (Das bedeutet, der Benutzer hat nichts eingegeben/ausgewählt)
                          if (!currentValue && !stateValue && originalValue) {
                            applyEditEinflussValueChange(einflussname, originalValue);
                          }
                          // Cleanup
                          if (focusedEinflussRef.current === einflussname) {
                            focusedEinflussRef.current = null;
                          }
                        }}
                        onInput={(e) => {
                          // onInput wird auch bei datalist-Auswahl ausgelöst
                          const newValue = e.target.value;
                          // Aktualisiere den ursprünglichen Wert, damit onBlur weiß, dass sich etwas geändert hat
                          if (originalEinflussValuesRef.current[einflussname] !== undefined) {
                            originalEinflussValuesRef.current[einflussname] = newValue;
                          }
                          applyEditEinflussValueChange(einflussname, newValue);
                        }}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          // Wenn der Benutzer tippt, wird der ursprüngliche Wert überschrieben
                          if (originalEinflussValuesRef.current[einflussname] !== undefined) {
                            originalEinflussValuesRef.current[einflussname] = newValue;
                          }
                          applyEditEinflussValueChange(einflussname, newValue);
                        }}
                      />
                      {messwerte.length > 0 && (
                        <datalist id={`compact-edit-datalist-${idx}`}>
                          {messwerte.map((mw, mwIdx) => (
                            <option key={mwIdx} value={mw || ''} />
                          ))}
                        </datalist>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Foto-Upload und -Anzeige */}
          <div className="compact-edit-field">
            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>{t('compact.fotos')}</label>
            <div className="photo-upload-section">
              <input
                type="file"
                accept="image/*"
                capture="camera"
                onChange={handlePhotoUpload}
                disabled={isUploadingPhoto || !selectedSession}
                style={{ display: 'none' }}
                ref={photoInputRef}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => photoInputRef.current?.click()}
                disabled={isUploadingPhoto || !selectedSession}
                style={{ cursor: 'pointer', display: 'inline-block' }}
              >
                {isUploadingPhoto ? `📷 ${t('compact.photo_uploading')}` : `📷 ${t('compact.photo_add')}`}
              </button>
              {editPhotos.length > 0 && (
                <div className="photo-preview-list" style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {editPhotos.map((photo) => (
                    <div key={photo.id} className="photo-preview-item" style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={photo.fileData}
                        alt={photo.fileName}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '2px solid #e5e7eb'
                        }}
                        title="Klicken zum Vergrößern"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(photo.id)}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title={t('compact.photo_delete_title')}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="compact-edit-info">
            <div>FZ: {formatTime(measurement.progressTime || 0)}</div>
            <div>EZ: {formatTime(measurement.individualTime || 0)}</div>
            {measurement.resumeNumber && (
              <div>WA-Nr: {measurement.resumeNumber}</div>
            )}
          </div>
        </div>

        <div className="compact-edit-actions">
          <button 
            className="compact-edit-btn compact-edit-btn-primary" 
            onClick={handleSave}
          >
            {t('compact.save')}
          </button>
          <button 
            className="compact-edit-btn compact-edit-btn-secondary" 
            onClick={onCancel}
          >
            {t('dialog.cancel')}
          </button>
          <button 
            className="compact-edit-btn compact-edit-btn-danger" 
            onClick={handleDelete}
          >
            {t('dialog.delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompactEditDialog;
